import React, { useState, useEffect, useRef, useContext, forwardRef, useImperativeHandle } from 'react';
import DataTable from '../../ui_elements/DataTable';
import { InputText } from 'primereact/inputtext';
import {
	CircularProgress,
	Backdrop,
	Button,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	TextField,
	InputAdornment,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import { makeStyles } from '@material-ui/core/styles';
import { isEqual, debounce } from 'lodash';
import { updateDataSpaceStateNew } from '../../../api_helper/api';
import FilterInitializer from './FilterInitializer';
import ColumnFactory from './ColumnFactory';
import ViewManager from './ViewManager';
import CalculatedColumnPanel from './CalculatedColumnPanel';
import CalculatedColumnEditor from './CalculatedColumnEditor';
import { Prompt } from 'react-router-dom';
import { DataSpaceViewContext } from '../context/DataSpaceViewContext';
import getLogo from '../../../utils/getLogo';
import { UserProfileContext } from '../../../UserProfileContext';
import { trackEvent } from '../../../utils/eventTracking';
import DataSpaceViewHeader from './DataSpaceViewHeader';

import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primeicons/primeicons.css';

const useStyles = makeStyles((theme) => ({
	dataTable: {
		// Grey border separating frozen and non-frozen columns
		'& .p-datatable-scrollable-view.p-datatable-unfrozen-view': {
			borderLeft: '2px solid #F8F9FA',
			boxSizing: 'content-box',
		},
		'& .p-datatable-scrollable-wrapper': {
			overflowX: 'hidden',
		},
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
	inputNumber: {
		marginLeft: '1px',
		marginRight: '1px',
	},
	seeDetailsButton: {
		color: 'black',
		marginLeft: '0.5rem',
	},
	dataSpaceHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
	},
	dataSpaceHeaderLeftWrapper: {
		display: 'flex',
		alignItems: 'center',
	},
	dataSpaceHeaderRightWrapper: {
		display: 'flex',
	},
	dataspaceViewManagerWrapper: {
		display: 'flex',
		alignItems: 'start',
		flexWrap: 'wrap',
		gap: '0.5rem',
		padding: '0.5rem 0.25rem 0 0.25rem',
		'@media (max-width: 768px)': {
			flexWrap: 'nowrap',
		},
	},
	datasetField: {
		width: '100%',
		height: 'fit-content',
		padding: '0.3rem',
		backgroundColor: '#C4C4C4',
		borderRadius: '0.2rem',
		transition: '0.3s',
		'&:hover': {
			backgroundColor: '#C4C4C4',
			filter: 'brightness(0.75)',
		},
		'& > span': {
			width: '100%',
			display: 'inline',
			color: '#000000',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
	},
	datasetFieldSelected: {
		backgroundColor: '#0DBC42',
		'&:hover': {
			backgroundColor: '#0DBC42',
			filter: 'brightness(0.75)',
		},
	},
	paginator: {
		padding: '0.5rem 0',
	},
}));

export default forwardRef(function PrimeDataTable(props, ref) {
	const classes = useStyles();

	const viewManagerRef = useRef(null);

	const dataSpaceViewContext = useContext(DataSpaceViewContext);
	const userProfile = useContext(UserProfileContext)[0];

	// Global Filter
	const [globalFilter, setGlobalFilter] = useState(null);

	// Refs
	const dataTableRef = useRef(null); // DataTable Ref
	const internalStateRef = useRef(undefined); // Used to track internal table state (ITS) to detect changes in ITS

	// Table
	const [internalTableState, setInternalTableState] = useState(props.internalTableState);
	const paginationOptions = [10, 25, 50, 100, 250];
	const defaultPaginationOption = 50;

	// Columns and Filters
	const [columnFilterTypes, setColumnFilterTypes] = useState([]);
	const [columnFilterStates, setColumnFilterStates] = useState({});
	const filterInitializer = new FilterInitializer(props.rows, props.columns);

	// Calculated columns
	const [columnEditorOpen, setColumnEditorOpen] = useState(false);
	const [columnEditorDetails, setColumnEditorDetails] = useState({});
	const handleEditCalculatedColumn = (field, header) => {
		setColumnEditorDetails({ field, header });
		setColumnEditorOpen(true);
	};
	const [numNewCalculatedColumns, setNumNewCalculatedColumns] = useState(0);

	const [selectColumnsDialogSearchInput, setSelectColumnsDialogSearchInput] = useState('');

	// Callback passed down to column header to allow user to hide column from context menu
	const onHideColumn = (field) => {
		trackEvent({
			userDetails: { userId: userProfile._id, email: userProfile.email },
			eventDetails: {
				types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
				eventName: 'WebApp_User shows/hides DataSpace column',
			},
		});

		// Prevent user from hiding all columns
		if (props.selectedColumns.length === 1) {
			return;
		}

		// Get current visible columns
		let updatedCols = [...props.selectedColumns];
		// Remove column to hide from current visible columns
		updatedCols.splice(
			updatedCols.findIndex((col) => col.field === field),
			1,
		);
		let updatedColumnWidths = dataSpaceViewContext.dataSpace.internalState?.columnWidths.split(',');
		updatedColumnWidths.splice(
			updatedCols.findIndex((col) => col.field === field),
			1,
		);
		updatedColumnWidths = updatedColumnWidths.join(',');
		props.setSelectedColumns(updatedCols);

		// Save current visible columns in database
		updatedCols = updatedCols.map((x) => x.field); // Convert to array of Strings representing fields

		dataSpaceViewContext.setDataSpace((prevState) => {
			const updatedState = { ...prevState };
			updatedState.frozenColumns =
				updatedState.frozenColumns && updatedState.frozenColumns.filter((col) => col !== field);
			updatedState.toggledColumnsState = updatedCols;
			updatedState.internalState = {
				...prevState.internalState,
				columnOrder: updatedCols,
				columnWidths: updatedColumnWidths,
			};
			return updatedState;
		});
	};

	const onFreezeColumn = (field) => {
		trackEvent({
			userDetails: { userId: userProfile._id, email: userProfile.email },
			eventDetails: {
				types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
				eventName: 'User clicked pin column button.',
			},
		});

		// Get current visible columns
		let updatedCols = [...props.selectedColumns];
		let updatedColumnWidths = dataSpaceViewContext.dataSpace.internalState?.columnWidths.split(',');

		// Move column matching field to beginning of array.
		let index = updatedCols.findIndex((col) => col.field === field);
		if (index !== -1) {
			let column = updatedCols.splice(index, 1)[0];
			updatedCols.unshift(column);
			let columnWidth = updatedColumnWidths.splice(index, 1)[0];
			updatedColumnWidths.unshift(columnWidth);
			updatedColumnWidths = updatedColumnWidths.join(',');
		}

		// Save current visible columns in database
		props.setSelectedColumns(updatedCols);
		updatedCols = updatedCols.map((x) => x.field); // Convert to array of Strings representing fields

		dataSpaceViewContext.setDataSpace((prevState) => {
			const updatedState = { ...prevState };
			updatedState.frozenColumns = updatedState.frozenColumns ? [...updatedState.frozenColumns, field] : [field];
			updatedState.toggledColumnsState = updatedCols;
			updatedState.internalState = {
				...prevState.internalState,
				columnOrder: updatedCols,
				columnWidths: updatedColumnWidths,
			};
			return updatedState;
		});

		dataTableRef.current.restoreTableState({
			...dataTableRef.current.state,
			columnOrder: updatedCols,
		});
	};

	const onUnfreezeColumn = (field) => {
		trackEvent({
			userDetails: { userId: userProfile._id, email: userProfile.email },
			eventDetails: {
				types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
				eventName: 'User clicked unpin column button.',
			},
		});

		// Get current visible columns
		let updatedCols = [...props.selectedColumns];
		let updatedColumnWidths = dataSpaceViewContext.dataSpace.internalState?.columnWidths.split(',');

		const finalIndex = dataSpaceViewContext.dataSpace.frozenColumns.length - 1;

		let index = updatedCols.findIndex((col) => col.field === field);
		if (index !== -1) {
			let column = updatedCols.splice(index, 1)[0];
			updatedCols.splice(finalIndex, 0, column);

			let columnWidth = updatedColumnWidths.splice(index, 1)[0];
			updatedColumnWidths.splice(finalIndex, 0, columnWidth);
			updatedColumnWidths = updatedColumnWidths.join(',');
		}

		// Save current visible columns in database
		props.setSelectedColumns(updatedCols);
		updatedCols = updatedCols.map((x) => x.field); // Convert to array of Strings representing fields

		dataSpaceViewContext.setDataSpace((prevState) => {
			const updatedState = { ...prevState };
			updatedState.frozenColumns = updatedState.frozenColumns.filter((col) => col !== field);
			updatedState.toggledColumnsState = updatedCols;
			updatedState.internalState = {
				...prevState.internalState,
				columnOrder: updatedCols,
				columnWidths: updatedColumnWidths,
			};
			return updatedState;
		});

		dataTableRef.current.restoreTableState({
			...dataTableRef.current.state,
			columnOrder: updatedCols,
			columnWidths: updatedColumnWidths,
		});
	};

	// Handles deletion of a custom column from the DataSpace frontend
	function handleDeleteColumn(field) {
		// Remove from selected columns
		props.setSelectedColumns((prev) => {
			const i = prev.findIndex((c) => c.field === field);
			if (i) prev.splice(i, 1);
			return prev;
		});

		// Need to remove column order from dataspace state or it will reupload to db
		const tableState = dataTableRef.current.state;
		if (tableState.columnOrder) {
			const i = tableState.columnOrder.findIndex((o) => o === field);
			if (i >= 0) tableState.columnOrder.splice(i, 1);
		}
		dataTableRef.current.restoreTableState(tableState);

		// Reloads DataSpace data
		props.deleteCustomColumn();
	}

	// Instance of ColumnFactory class which creates columns dynamically based on props
	const columnFactory = new ColumnFactory(
		dataTableRef,
		columnFilterStates,
		setColumnFilterStates,
		props.rows,
		props.renameHeader,
		dataSpaceViewContext,
		columnFilterTypes,
		dataSpaceViewContext.dataSpace.frozenColumns || [],
		onHideColumn,
		onFreezeColumn,
		onUnfreezeColumn,
		handleEditCalculatedColumn,
		handleDeleteColumn,
		props.isEditingDisabled,
	);

	// Retrieve the properties representing the user's current view
	const getCurrentView = () => {
		const view = {
			internalProperties: {},
			externalProperties: {},
		};

		view.internalProperties = {
			state: dataTableRef.current.state,
		};

		view.externalProperties = {
			selectedColumns: props.selectedColumns,
			filterStates: columnFilterStates,
			frozenColumns: dataSpaceViewContext.dataSpace.frozenColumns || [],
		};

		return view;
	};

	// Load the properties of a saved view into DataSpace
	const loadView = (view) => {
		// Set internal properties
		if (view.internalProperties && view.internalProperties.state) {
			view.internalProperties.state.first = 0; // Reset first (page) to 0 to avoid pagination issues;
			// Set internal properties
			if (dataTableRef.current && dataTableRef.current.restoreTableState) {
				dataTableRef.current.restoreTableState(view.internalProperties.state);
			}
		}

		// Set external properties
		if (view.externalProperties) {
			if (view.externalProperties.selectedColumns) {
				props.setSelectedColumns(view.externalProperties.selectedColumns);
			}
			setColumnFilterStates(() => {
				const filterStates = filterInitializer.getInitialFilterState();

				if (view.externalProperties.filterStates) {
					for (let field in view.externalProperties.filterStates) {
						filterStates[field] = view.externalProperties.filterStates[field];
					}
				}

				return filterStates;
			});
			dataSpaceViewContext.setDataSpace((prevState) => ({
				...prevState,
				isCurrentViewSaved: true,
				frozenColumns: view.externalProperties.frozenColumns,
			}));
		}
	};

	// Load table's internal state from props
	useEffect(() => {
		if (props.internalTableState) {
			setInternalTableState(props.internalTableState);
		}
	}, [props.internalTableState]);

	// Initialize filter states
	useEffect(() => {
		if (props.columns.length > 0) {
			// Initialize filter states
			setColumnFilterTypes(filterInitializer.getColumnFilterTypes());

			if (props.initialFilterStates) {
				setColumnFilterStates(props.initialFilterStates);
			} else {
				setColumnFilterStates((prev) => {
					const initialState = filterInitializer.getInitialFilterState();
					if (Object.keys(prev).length === 0) return initialState;

					// Merge missing keys (e.g. from new columns)
					const newState = { ...prev };
					let modified = false;
					Object.keys(initialState).forEach((key) => {
						if (newState[key] === undefined) {
							newState[key] = initialState[key];
							modified = true;
						}
					});

					return modified ? newState : prev;
				});
			}
		}

		// Update selected columns to reflect any header changes
		if (props.columns.length > 0) {
			let selColsCopy = [...props.selectedColumns];
			selColsCopy = selColsCopy
				.map((sCol) => {
					const col = props.columns.find((c) => c.field === sCol.field);
					if (!col) return null;
					const { header, dataSourceName, columnToggleLabel } = col;
					return {
						columnToggleLabel: columnToggleLabel,
						dataSourceName: dataSourceName,
						field: sCol.field,
						header: header,
					};
				})
				.filter((x) => x !== null);

			props.setSelectedColumns(selColsCopy);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.columns, props.initialFilterStates, props.rows]);

	// State management in DataTable
	const onCustomSaveState = (state) => {
		/*
        Note: Modifying the globalFilter activates onCustomSaveState twice.
        The reason for this is currently unknown.
        On the first iteration state.filters has the previous state's value, and thus
        the following check fails and an unneccessary backend call is made. This call
        however is not destructive, as we remove the filter property before updating the
        state in the DataBase.

        I have submitted a question to PrimeFaces regarding this
        https://forum.primefaces.org/viewtopic.php?f=57&t=65458
    */

		// Avoid unnecessary backend calls if change in state is due to filter modification,
		// or if table's state has not changed.
		// We do not persist filter modifications implicitly, only by user action
		let filtersUnchanged = isEqual(internalStateRef.current?.filters, state.filters);
		let sortUnchanged = isEqual(internalStateRef.current?.multiSortMeta, state.multiSortMeta);
		const internalStateHasChanged = !isEqual(
			{ ...internalStateRef.current, columnWidths: '' },
			{ ...state, columnWidths: '' },
		);

		if (internalStateRef.current && !filtersUnchanged) {
			trackEvent({
				userDetails: { userId: userProfile._id, email: userProfile.email },
				eventDetails: {
					types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
					eventName: 'WebApp_User filters DataSpace column',
				},
			});
		}

		if (internalStateRef.current?.multiSortMeta && !sortUnchanged) {
			trackEvent({
				userDetails: { userId: userProfile._id, email: userProfile.email },
				eventDetails: {
					types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
					eventName: 'WebApp_User sorts DataSpace column',
				},
			});
		}

		if (
			(filtersUnchanged && internalStateHasChanged && internalStateRef.current !== undefined) ||
			(props.selectedColumns && props.selectedColumns?.length !== state.columnOrder?.length)
		) {
			// If columns are added/removed, update state.
			if (props.selectedColumns && props.selectedColumns?.length !== state.columnOrder?.length) {
				state.columnOrder = props.selectedColumns.map((column) => column.field);
			} else {
				props.setSelectedColumns(() =>
					state.columnOrder.map((column) => {
						return props.columns.find((e) => e.field === column);
					}),
				);
			}

			const payload = {
				mode: 'currentState',
				dataSpaceId: props.dataSpaceId,
				internalState: state,
				frozenColumns: dataSpaceViewContext.dataSpace.frozenColumns || [],
				toggledColumnsState: props.selectedColumns.map((col) => col.field),
			};

			if (!props.isEditingDisabled) {
				updateDataSpaceStateNew(payload);
			}

			dataSpaceViewContext.setDataSpace((prevState) => ({
				...prevState,
				internalState: state,
				isCurrentViewSaved: false,
			}));

			dataTableRef.current.restoreTableState({
				...dataTableRef.current.state,
				columnOrder: state.columnOrder,
			});
		}

		internalStateRef.current = state; // Track internal table state
	};

	// State management in DataTable
	const onCustomRestoreState = () => {
		return internalTableState;
	};

	// Map selected columns to Column components for display in table
	const columnComponents = props.selectedColumns.map((col) => {
		return columnFactory.getColumnComponent(col);
	});

	// Extract numeric columns for use in Calculated Column Panel
	const numericalColumns = (function getNumericalColumns() {
		const numericalColumnsByField = columnFilterTypes
			.filter((x) => x.filterType === 'numericRange')
			.map((x) => x.field);
		return props.columns.filter((x) => numericalColumnsByField.includes(x.field));
	})();

	// This function is used to integrate a new Calculated Column returned from the CalculatedColumnPanel into
	// the DataSpace.
	// It initializes filters for the new column and calls another function passed down from the parent component to
	// which integrates the column and row data into the DataSpace
	const handleAddCalculatedColumn = (colDetails) => {
		const { field } = colDetails.calcColDetails;
		initializeFilterForNewColumn(field);
		props.appendCalculatedColumn(colDetails);

		function initializeFilterForNewColumn(field) {
			setColumnFilterTypes((prev) => {
				const newTypes = [...prev];
				newTypes.push({ field, filterType: 'numericRange' });
				return newTypes;
			});
			setColumnFilterStates((prev) => {
				const newStates = { ...prev };
				newStates[field] = { min: null, max: null };
				return newStates;
			});
		}

		// Initiate logic to add new column to selected columns.
		// Cannot perform this logic in this function as it relies on the parent's state to change
		// and for logic to be done by the parent based on that change. The result of that logic
		// is not available within this function call.
		// See useEffect below.
		setNumNewCalculatedColumns((prev) => {
			return prev + 1;
		});
	};

	// If the number of new calculated columns changes (increases), add the new calculated column
	// to the list of selected columns.
	useEffect(() => {
		// Do not act if no columns have been created
		if (numNewCalculatedColumns !== 0) {
			let updatedColumns = [...props.selectedColumns];
			updatedColumns.push(props.columns[props.columns.length - 1]); // The last element in props.columns will be the new calculated column
			props.setSelectedColumns(updatedColumns);
		}
	}, [numNewCalculatedColumns]);

	// Prevent back-swipe navigation gesture on mobile devices
	useEffect(() => {
		// Check if the device is a mobile device by seeing if it has mouse hover capabilities or not
		const hasMouseCapabilities = !Boolean(window.matchMedia('(any-hover:none)').matches);

		// If device has mouse capabilities or the dataTable isn't rendered, return
		if (hasMouseCapabilities || !internalTableState) {
			return;
		}

		const datatableWrapper = document.querySelector('#datatable-dataspace');

		if (!datatableWrapper) return;

		const filterEdgeTouches = (e) => {
			const threshold = 20;

			// Support both standard touches and changedTouches
			const touch = e.touches[0] || e.changedTouches[0];

			// Guard clause if no touch point exists
			if (!touch) return;

			const rect = datatableWrapper.getBoundingClientRect();
			const x = touch.clientX;
			const y = touch.clientY;

			// Distance from each edge (inside the element)
			const distLeft = x - rect.left;
			const distRight = rect.right - x;
			const distTop = y - rect.top;
			const distBottom = rect.bottom - y;

			const isNearEdge =
				(distLeft >= 0 && distLeft <= threshold) ||
				(distRight >= 0 && distRight <= threshold) ||
				(distTop >= 0 && distTop <= threshold) ||
				(distBottom >= 0 && distBottom <= threshold);

			if (isNearEdge) {
				if (e.cancelable) e.preventDefault();
				e.stopPropagation();
			}
		};

		datatableWrapper.addEventListener('touchstart', filterEdgeTouches, { passive: false });

		return () => {
			datatableWrapper.removeEventListener('touchstart', filterEdgeTouches);
		};
	}, [internalTableState]);

	useEffect(() => {
		const headers = document.getElementsByClassName('p-datatable-thead');

		if (headers.length > 1) {
			if (headers[0]?.children[0]?.offsetHeight > headers[1]?.children[0]?.offsetHeight) {
				headers[1].children[0].style.height = headers[0].children[0].offsetHeight + 'px';
			} else if (headers[0]?.children[0]?.offsetHeight < headers[1]?.children[0]?.offsetHeight) {
				headers[0].children[0].style.height = headers[1].children[0].offsetHeight + 'px';
			}

			if (headers[0]?.children[1]?.offsetHeight > headers[1]?.children[1]?.offsetHeight) {
				headers[1].children[1].style.height = headers[0].children[1].offsetHeight + 'px';
			} else if (headers[0]?.children[1]?.offsetHeight < headers[1]?.children[1]?.offsetHeight) {
				headers[0].children[1].style.height = headers[1].children[1].offsetHeight + 'px';
			}
		}
	});

	// Expose methods to parent component
	useImperativeHandle(ref, () => ({
		saveViewClickHandler: viewManagerRef.current?.saveViewClickHandler,
		createViewClickHandler: viewManagerRef.current?.createViewClickHandler,
		loadView,
	}));

	return (
		<div className="dataspace-wrapper">
			<CalculatedColumnEditor
				open={columnEditorOpen}
				handleClose={() => setColumnEditorOpen(false)}
				columns={numericalColumns}
				columnDetails={columnEditorDetails}
				dataSpaceId={props.dataSpaceId}
				applyModifiedCustomColumn={props.applyModifiedCustomColumn}
				displayAlert={props.displayAlert}
			/>

			<CalculatedColumnPanel
				open={props.calcColPanelOpen}
				handleClose={props.handleCloseCalcColPanel}
				columns={numericalColumns}
				dataSpaceId={props.dataSpaceId}
				handleAddCalculatedColumn={handleAddCalculatedColumn}
				displayAlert={props.displayAlert}
			/>

			<div className="card dataspace-viewmanager-wrapper">
				<DataSpaceViewHeader
					dataSpaceId={props.dataSpaceId}
					getCurrentView={getCurrentView}
					loadView={loadView}
					setIsViewLoading={props.setLoadingOpen}
					callToActionButton={props.callToActionButton}
				>
					{props.children}
				</DataSpaceViewHeader>

				<div className={props.isDataSpaceExpanded ? 'dataspace-container-fullscreen' : 'dataspace-container'}>
					<div className={classes.dataspaceViewManagerWrapper} style={{ backgroundColor: '#f8f9fa' }}>
						<ViewManager
							ref={viewManagerRef}
							dataSpaceId={props.dataSpaceId}
							getCurrentView={getCurrentView}
							loadView={loadView}
							setIsViewLoading={props.setLoadingOpen}
							globalFilter={globalFilter}
							setGlobalFilter={setGlobalFilter}
							isEditingDisabled={props.isEditingDisabled}
						/>
						{/* The "Add New Column/Refresh Dataspace" section is added as props.children so that it can be rendered in the 
            same component as "Save View/Load View" so that those sections can be rendered in the same row. */}
						{props.children}
					</div>

					<div className={'datatable-wrapper'}>
						<DataTable
							ref={dataTableRef}
							paginator
							paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
							paginatorClassName={classes.paginator}
							paginatorDropdownAppendTo="self"
							currentPageReportTemplate="{currentPage}"
							rowsPerPageOptions={paginationOptions}
							//rows={10} // Due to table state system, default rows/page is implemented after table state retrieved from backend
							id="datatable-dataspace"
							className={classes.dataTable + ' p-datatable-sm'}
							size="small"
							value={props.rows}
							scrollable
							resizableColumns
							reorderableColumns
							columnResizeMode="expand"
							onStateSa
							stateStorage="custom"
							customSaveState={onCustomSaveState}
							customRestoreState={onCustomRestoreState}
							sortMode="multiple"
							globalFilter={globalFilter}
							frozenWidth={(() => {
								let frozenWidth = 0;
								for (let column of dataSpaceViewContext.dataSpace.frozenColumns || []) {
									const index = dataSpaceViewContext.dataSpace.internalState?.columnOrder.findIndex(
										(x) => x === column,
									);
									const width = dataSpaceViewContext.dataSpace.internalState?.columnWidths.split(',')[index] || '160';
									frozenWidth += parseInt(width);
								}
								return frozenWidth + 'px';
							})()}
						>
							{columnComponents}
						</DataTable>
					</div>
				</div>
			</div>
		</div>
	);
});
